/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useStudyMaterial } from '../context/StudyMaterialContext';
import { CourseCard } from '../components/CourseCard';
import { QuizCard } from '../components/QuizCard';
import { PracticeHubCard } from '../components/PracticeHubCard';
import { PracticeTestAccessModal } from '../components/PracticeTestAccessModal';
import { PREMIUM_COURSES, PremiumCourse } from '../data/coursesData';
import { getStudentAccessInfo } from '../utils/studentAccess';
import { motion } from 'motion/react';
import { 
  Flame, 
  Zap, 
  Award, 
  Trophy, 
  ArrowRight, 
  Sparkles, 
  ChevronRight, 
  HelpCircle,
  BrainCircuit,
  MessageSquare,
  Bookmark,
  BookOpen,
  BarChart3,
  GraduationCap,
  ClipboardList,
  Brain,
  Users,
  Target,
  Lock,
  FileText
} from 'lucide-react';

const premiumFeatures = [
  {
    title: 'NCERT-Focused Learning',
    text: '100% aligned with the latest NCERT syllabus for Classes 6, 7 & 8 with chapter-wise structured learning.',
    icon: GraduationCap,
    iconBg: 'bg-emerald-50 dark:bg-emerald-950/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    emoji: '📚'
  },
  {
    title: 'Smart Practice System',
    text: 'Daily Practice Tests, TERM I & TERM II Mock Tests with detailed answer explanations.',
    icon: ClipboardList,
    iconBg: 'bg-indigo-50 dark:bg-indigo-950/20',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    emoji: '📝'
  },
  {
    title: 'AI Performance Analysis',
    text: 'Identify strengths, weak chapters, accuracy, speed and receive personalized improvement suggestions.',
    icon: BrainCircuit,
    iconBg: 'bg-violet-50 dark:bg-violet-950/20',
    iconColor: 'text-violet-600 dark:text-violet-400',
    emoji: '📊'
  },
  {
    title: 'Gamified Learning Experience',
    text: 'Earn badges, maintain study streaks, climb the leaderboard and stay motivated every day.',
    icon: Trophy,
    iconBg: 'bg-amber-50 dark:bg-amber-950/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    emoji: '🏆'
  },
  {
    title: 'Premium Study Resources',
    text: 'Mind Maps, Study Notes, MCQs, Assertion & Reason, Case Studies, and True or False / Fill Ups practice.',
    icon: Brain,
    iconBg: 'bg-fuchsia-50 dark:bg-fuchsia-950/20',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-400',
    emoji: '🧠'
  },
  {
    title: 'Parent Progress Dashboard',
    text: 'Parents can monitor learning progress, practice consistency and academic improvement with clear reports.',
    icon: Users,
    iconBg: 'bg-rose-50 dark:bg-rose-950/20',
    iconColor: 'text-rose-600 dark:text-rose-400',
    emoji: '👨‍👩‍👧‍👦'
  },
  {
    title: 'Exam Success Strategy',
    text: 'Chapter-wise preparation, revision support and smart practice designed to build confidence before school exams.',
    icon: Target,
    iconBg: 'bg-sky-50 dark:bg-sky-950/20',
    iconColor: 'text-sky-600 dark:text-sky-400',
    emoji: '🎯'
  },
  {
    title: 'Safe & Distraction-Free Learning',
    text: 'A clean, focused and secure study environment built exclusively for academic success.',
    icon: Lock,
    iconBg: 'bg-blue-50 dark:bg-blue-950/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    emoji: '🔒'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      type: 'spring' as const, 
      stiffness: 80, 
      damping: 15 
    } 
  }
};

export const Home: React.FC = () => {
  const { user, quizzes, setPage, addNotification, addToCart, promotions, setAuthModalOpen, guardPurchase, updateUserProfile, activateFreePremiumOnServer } = useApp();
  const { classes, subjects } = useStudyMaterial();

  const [notified, setNotified] = useState(false);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [premiumPopupType, setPremiumPopupType] = useState<'daily' | 'mock' | 'course'>('daily');
  const [purchaseTargetClass, setPurchaseTargetClass] = useState<number>(8);
  const [purchaseTargetSubject, setPurchaseTargetSubject] = useState<string>('Science');

  const accessInfo = getStudentAccessInfo(user);
  const isGuest = !user || user.role !== 'student';

  const handleJoinPremium = () => {
    setShowPremiumPopup(false);
    guardPurchase(() => {
      const subNameLower = purchaseTargetSubject.toLowerCase();
      let subKey = 'sci';
      if (subNameLower.includes('math')) {
        subKey = 'math';
      } else if (subNameLower.includes('social') || subNameLower.includes('history') || subNameLower.includes('geo') || subNameLower.includes('civic') || subNameLower.includes('eco')) {
        subKey = 'social';
      }
      const courseId = `${purchaseTargetClass}-${subKey}`;
      addToCart(courseId);
    }, 'Please Login to Unlock Course', 'login');
  };

  const isLaunchOfferActive = () => {
    if (!promotions || promotions.free_premium_active !== 'true') return false;
    const now = new Date();
    if (promotions.free_premium_start_date) {
      const start = new Date(promotions.free_premium_start_date);
      if (now < start) return false;
    }
    if (promotions.free_premium_end_date) {
      const end = new Date(promotions.free_premium_end_date);
      if (now > end) return false;
    }
    return true;
  };

  const launchOfferActive = isLaunchOfferActive();

  const handleOpenPdfLibrary = async (courseId: string) => {
    if (!user) {
      guardPurchase(() => handleOpenPdfLibrary(courseId), 'Please Login to Access Study Material PDF', 'login');
      return;
    }
    const parts = courseId.split('-');
    const classNum = parts[0] || '6';
    const subjectKey = parts[1] || 'sci';

    const targetClassName = `Class ${classNum}`;
    let targetSubjectName = 'Science';
    if (subjectKey === 'math') targetSubjectName = 'Mathematics';
    else if (subjectKey === 'social') targetSubjectName = 'Social Science';

    try {
      const classRes = await fetch('/api/master/classes');
      if (classRes.ok) {
        const classes = await classRes.json();
        const matchedClass = classes.find((c: any) => 
          String(c.class_name).replace(/\D/g, '') === classNum
        ) || { id: classNum, class_name: targetClassName };

        const subjectRes = await fetch(`/api/master/subjects?class_id=${matchedClass.id}`);
        if (subjectRes.ok) {
          const subjects = await subjectRes.json();
          const matchedSubject = subjects.find((s: any) => {
            const sName = String(s.subject_name).toLowerCase();
            if (subjectKey === 'math') return sName.includes('math') || sName.includes('precision');
            if (subjectKey === 'social') return sName.includes('social') || sName.includes('soc');
            return sName === 'science' || sName.includes('sci');
          }) || { id: subjectKey === 'math' ? '1' : subjectKey === 'social' ? '3' : '2', subject_name: targetSubjectName };

          setPage('pdf-library', {
            pdfClassId: String(matchedClass.id),
            pdfSubjectId: String(matchedSubject.id),
            pdfClassName: matchedClass.class_name,
            pdfSubjectName: matchedSubject.subject_name || targetSubjectName
          });
          return;
        }
      }
    } catch (error) {
      console.error('Failed to resolve master academic data for PDF library routing:', error);
    }

    setPage('pdf-library', {
      pdfClassId: classNum,
      pdfSubjectId: subjectKey === 'math' ? '1' : subjectKey === 'social' ? '3' : '2',
      pdfClassName: targetClassName,
      pdfSubjectName: targetSubjectName
    });
  };

  let displayedPremiumCourses: PremiumCourse[] = [];
  let showComingSoon = false;

  if (isGuest) {
    // Guest: Show only three featured Premium Course Cards in the specified order:
    // 1. NCERT Class 6 Science (id: '6-sci')
    // 2. NCERT Class 7 Mathematics (id: '7-math')
    // 3. NCERT Class 8 Social Science (id: '8-social')
    const orderedIds = ['6-sci', '7-math', '8-social'];
    displayedPremiumCourses = orderedIds
      .map(id => PREMIUM_COURSES.find(c => c.id === id))
      .filter((c): c is PremiumCourse => !!c);
  } else {
    // Logged in student: automatically detect registered class
    const studentClass = user.currentClass || '';
    displayedPremiumCourses = PREMIUM_COURSES.filter(c => c.class === studentClass);

    if (displayedPremiumCourses.length === 0) {
      showComingSoon = true;
    }
  }

  const handleNotifyMe = () => {
    setNotified(true);
    addNotification(
      'Notification Set!',
      `We will notify you immediately when premium courses are launched for ${user?.currentClass || 'your class'}.`,
      'announcement'
    );
  };

  const featuredQuizzes = quizzes.slice(0, 3);

  return (
    <div className="space-y-20 pb-20">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-16 sm:pb-24 lg:pt-20">
        {/* Glow Spheres */}
        <div className="absolute top-0 left-1/2 -z-10 h-[400px] w-[600px] -translate-x-1/2 bg-indigo-500/10 blur-[120px] dark:bg-indigo-500/5"></div>
        <div className="absolute top-1/4 right-10 -z-10 h-[300px] w-[300px] bg-sky-400/10 blur-[80px] dark:bg-sky-500/5"></div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-8">

          {/* Admin Controlled Limited-Time Offer Badge */}
          {promotions && promotions.show_homepage_badge === 'true' && promotions.homepage_badge_title && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-xl mx-auto mb-6 p-4 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 text-white shadow-xl shadow-indigo-500/10 border border-indigo-500/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-left"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="bg-amber-400 text-slate-950 text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full animate-bounce">
                    Offer
                  </span>
                  <h4 className="font-display font-black text-xs uppercase tracking-wider text-amber-300">
                    {promotions.homepage_badge_title}
                  </h4>
                </div>
                {promotions.homepage_badge_subtitle && (
                  <p className="text-xs font-medium text-slate-100">
                    {promotions.homepage_badge_subtitle}
                  </p>
                )}
                {promotions.homepage_badge_end_date && (
                  <p className="text-[10px] text-indigo-200 font-mono">
                    Valid till: {new Date(promotions.homepage_badge_end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
              <button 
                onClick={() => {
                  const dest = promotions.homepage_badge_cta_destination || 'courses';
                  setPage(dest as any);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-white text-indigo-900 font-extrabold text-xs rounded-xl hover:bg-slate-100 cursor-pointer text-center whitespace-nowrap shrink-0 transition-colors"
              >
                {promotions.homepage_badge_cta_text || "View Offer"}
              </button>
            </motion.div>
          )}
          
          {/* Animated Announcement Chip */}
          <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 px-3.5 py-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
            <Sparkles className="h-3.5 w-3.5 text-indigo-500 animate-pulse" />
            <span>✨ India's Premium Academic Study Resource Platform</span>
          </div>

          <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight text-slate-900 dark:text-white max-w-4xl mx-auto leading-none">
            Master Every Chapter.<br className="hidden sm:inline" />{' '}
            <span className="bg-gradient-to-r from-sky-500 via-indigo-500 to-indigo-600 bg-clip-text text-transparent">
              Excel Every Exam.
            </span>
          </h1>

          <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-base sm:text-xl text-slate-500 dark:text-slate-400 font-medium">
              Premium Study and Practice Resources that help Academic students learn smarter, practice consistently, and score higher.
            </p>
            <p className="text-sm font-semibold tracking-wide text-indigo-600 dark:text-indigo-400 font-display flex items-center justify-center gap-1 flex-wrap">
              <span className="bg-gradient-to-r from-sky-500 to-indigo-600 bg-clip-text text-transparent font-extrabold tracking-tight flex items-start">
                MCQ PULSE<span className="text-[9px] text-indigo-500 align-super leading-none ml-0.5 mt-0.5 font-bold">™</span>
              </span>
              <span>- Where Every Question Sparks Success.</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              onClick={() => setPage('courses')}
              className="px-6 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-200 dark:shadow-none hover:scale-[1.02] transition-all"
            >
              <span>Explore Study Resources</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button 
              onClick={() => {
                setPage('practice');
              }}
              className="px-6 py-3.5 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-xl font-bold hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.02] transition-all"
            >
              <span>Explore Practice Resources</span>
              <BrainCircuit className="h-4 w-4 text-indigo-500" />
            </button>
          </div>

          {/* First Chapter FREE Promotional Card & Install Image */}
          <div className="mt-12 mb-8 max-w-5xl mx-auto px-4 sm:px-0 flex flex-col md:flex-row items-stretch gap-6">
            
            {/* Install Image */}
            <div className="w-full md:w-1/2 flex items-center justify-center">
              <img 
                src="/instal.png" 
                alt="Install MCQ PULSE App" 
                className="w-full h-auto object-contain rounded-3xl"
              />
            </div>

            {/* First Chapter FREE Card */}
            <div className="w-full md:w-1/2 flex">
              <motion.div 
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (user) {
                    setPage('study-material');
                  } else {
                    setAuthModalOpen(true, 'signup');
                  }
                }}
                className="relative p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-sky-50 dark:from-slate-800/80 dark:via-slate-900 dark:to-slate-800/80 border border-indigo-100/80 dark:border-indigo-500/20 shadow-xl shadow-indigo-500/10 dark:shadow-none cursor-pointer overflow-hidden group flex-1 flex flex-col justify-center"
              >
                <div className="absolute top-0 right-0 p-32 bg-indigo-400/10 dark:bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-400/20 transition-all duration-500"></div>
                
                <div className="relative z-10 flex flex-col items-center justify-center gap-5 text-center w-full h-full">
                  <div className="flex-1 space-y-2.5 flex flex-col items-center justify-center">
                    <div className="inline-block px-3 py-1 bg-gradient-to-r from-indigo-600 to-sky-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest rounded-full shadow-sm">
                      First Chapter FREE
                    </div>
                    
                    <h3 className="font-display text-2xl sm:text-3xl font-black text-slate-900 dark:text-white leading-tight">
                      Start Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-sky-600 dark:from-indigo-400 dark:to-sky-400">FREE</span>
                    </h3>
                    
                    <p className="text-slate-600 dark:text-slate-300 font-medium text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
                      Get your First Chapter Study Material for free. Study Online + Download the Chapter PDF for Offline Learning.
                    </p>
                    
                    <div className="pt-3">
                      <span className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-bold hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group-hover:translate-x-1 duration-300">
                        {user ? 'Go to Study Materials' : 'Grab It Now — Register / Login'}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Social proof numbers */}
          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto text-center">
            <div>
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">📚 100+</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Study Packs</p>
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">📝 50,000+</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Practice Questions</p>
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">🎯 Daily</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Free Quizzes</p>
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">⭐ Regular</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Content Updates</p>
            </div>
          </div>

          {/* Admin Controlled Promotional Offer Banner Image */}
          {promotions && promotions.show_offer_image === 'true' && promotions.offer_image_url && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-12 max-w-4xl mx-auto rounded-3xl overflow-hidden border border-slate-200/50 dark:border-slate-800 shadow-2xl relative group bg-slate-950"
            >
              <img 
                src={promotions.offer_image_url} 
                alt="Special Academic Offer Banner" 
                className="w-full h-auto object-cover max-h-[360px] md:max-h-[420px] hover:scale-[1.01] transition-transform duration-500 mx-auto"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent pointer-events-none"></div>
            </motion.div>
          )}

        </div>
      </section>

      {/* Designed for Students. Trusted by Parents. Premium Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/30 dark:border-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-mono text-[10px] font-black uppercase tracking-widest">
            ⭐ Premium Platform Features
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 dark:text-white leading-tight tracking-tight">
            Designed for Students. <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent dark:from-indigo-400 dark:via-fuchsia-400 dark:to-indigo-400">Trusted by Parents.</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 font-medium">
            Everything your child needs to learn smarter, practise better and achieve academic excellence—all in one premium learning platform.
          </p>
        </div>

        {/* 8 Feature cards in 2 x 4 responsive grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {premiumFeatures.map((feat, idx) => {
            return (
              <motion.div
                key={idx}
                variants={cardVariants}
                whileHover={{ y: -6, scale: 1.02, transition: { duration: 0.2 } }}
                className="relative p-6 rounded-2xl bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border border-slate-200/50 dark:border-slate-800/40 shadow-sm hover:shadow-xl dark:hover:shadow-indigo-950/20 hover:border-indigo-500/30 dark:hover:border-indigo-400/30 transition-all duration-300 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">{feat.emoji}</span>
                      <h3 className="font-display font-extrabold text-sm sm:text-base text-slate-900 dark:text-white leading-snug">
                        {feat.title}
                      </h3>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                      {feat.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Premium Study Material Library Section for Logged-In Students */}
      {user && user.role === 'student' && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-12">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
              📚 STUDY PACK LIBRARY
            </span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 dark:text-white mt-1">
              Study Material
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm max-w-3xl mx-auto">
              Everything you need to master every NCERT chapter in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {subjects.filter(sub => sub.isActive && !['hindi', 'english', 'biology', 'chemistry', 'physics'].includes(sub.name.toLowerCase())).map(sub => {
              // Match student class
              const userClassStr = user?.currentClass || 'Class 6';
              const userClassNum = userClassStr.replace(/\D/g, '') || '6';

              const activeClass = classes.find(
                c => c.name.toLowerCase() === userClassStr.toLowerCase() ||
                     c.name.replace(/\D/g, '') === userClassNum ||
                     c.name.toLowerCase().includes(userClassStr.toLowerCase())
              ) || { id: `c_${userClassNum}`, name: `Class ${userClassNum}` };

              const classNum = activeClass.name.match(/\d+/)?.[0] || userClassNum;
              const subLower = sub.name.toLowerCase();
              let subCode = 's';
              if (subLower.includes('math')) {
                subCode = 'm';
              } else if (subLower.includes('social') || subLower.includes('soc')) {
                subCode = 'ss';
              }
              const subjectImage = `/images/courses/c${classNum}${subCode}1.webp`;

              return (
                <div 
                  key={sub.id}
                  className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-300 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group overflow-hidden flex flex-col h-full self-stretch"
                  style={{ border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                >
                  <div className="relative w-full overflow-hidden select-none bg-slate-950 aspect-[1498/451]">
                    <img 
                      src={subjectImage} 
                      alt={sub.name} 
                      width={1498}
                      height={451}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-300"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src.endsWith('.webp')) {
                          target.src = target.src.replace(/\.webp$/, '.jpg');
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                    <span className="absolute top-3.5 right-3.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-[9px] font-black px-2.5 py-1 rounded-full shadow-md flex items-center gap-1 uppercase tracking-widest">
                      ⭐ PREMIUM SYLLABUS
                    </span>
                  </div>

                  <div className="p-6 flex flex-1 flex-col justify-between space-y-6">
                    <div className="space-y-2">
                      <span className="font-mono font-black text-indigo-600 dark:text-indigo-400 text-[10px] uppercase tracking-wider">
                        Academic Core Curriculum
                      </span>
                      <h3 className="font-display font-extrabold text-base text-slate-900 dark:text-white leading-tight">
                        {activeClass.name} {sub.name}
                      </h3>
                      
                      <ul className="text-xs font-semibold text-slate-600 dark:text-slate-400 space-y-1.5 pt-2">
                        <li className="flex items-center gap-1.5">✔ Mind Maps</li>
                        <li className="flex items-center gap-1.5">✔ Study Notes</li>
                        <li className="flex items-center gap-1.5">✔ MCQs with Explanation</li>
                        <li className="flex items-center gap-1.5">✔ True or False / Fill Ups</li>
                        <li className="flex items-center gap-1.5">✔ Assertion & Reason</li>
                        <li className="flex items-center gap-1.5">✔ Case Studies</li>
                      </ul>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={() => {
                          localStorage.setItem('mp_selected_subject_id', sub.id);
                          setPage('study-material');
                        }}
                        className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-xl font-extrabold text-xs flex items-center justify-center gap-1.5 cursor-pointer shadow-md transition-all hover:scale-[1.01]"
                      >
                        <span>Open Study Library</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Featured Courses Catalog */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-baseline gap-4 mb-8">
          <div>
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">Academic Pathways</span>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 dark:text-white mt-1">
              NCERT Premium Courses
            </h2>
          </div>
          <button 
            onClick={() => setPage('courses')}
            className="text-xs font-bold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 flex items-center gap-1 group cursor-pointer"
          >
            <span>Browse Complete Catalog</span>
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>

        {showComingSoon ? (
          <div className="max-w-xl mx-auto bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-slate-800 p-8 text-center space-y-6 shadow-sm">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
              <Sparkles className="h-8 w-8 animate-pulse" />
            </div>
            <div className="space-y-2">
              <h3 className="font-display font-extrabold text-xl text-slate-900 dark:text-white">
                Premium Courses Coming Soon
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                We are preparing premium learning resources for your class. Stay connected with MCQ PULSE™ for upcoming releases.
              </p>
            </div>
            <button
              onClick={handleNotifyMe}
              disabled={notified}
              className={`px-6 py-3 rounded-xl font-bold text-xs cursor-pointer transition-all ${
                notified
                  ? 'bg-emerald-500 text-white'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              {notified ? '✓ Notified' : 'Notify Me'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
            {displayedPremiumCourses.map((course) => {
              return (
                <div 
                  key={course.id}
                  className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-300 dark:border-slate-700 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group overflow-hidden flex flex-col h-full self-stretch"
                  style={{ border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  id={`premium-home-card-${course.id}`}
                >
                  {/* HERO IMAGE BANNER */}
                  <div 
                    className="relative w-full overflow-hidden select-none bg-white aspect-[1498/451]"
                    style={{ borderRadius: '20px 20px 0 0' }}
                  >
                    <img 
                      src={course.image || "/images/courses/c6s1.webp"} 
                      alt={`${course.title} Banner`} 
                      width={1498}
                      height={451}
                      loading="lazy"
                      className="z-20 block w-full h-full object-contain object-center bg-white aspect-[1498/451]"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '1498 / 451',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        backgroundColor: 'white'
                      }}
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src.endsWith('.webp')) {
                          target.src = target.src.replace(/\.webp$/, '.jpg');
                        } else if (target.src.endsWith('/images/courses/c7s1.jpg')) {
                          target.src = '/c7s1.jpg';
                        } else if (target.src.endsWith('/images/courses/c7s2.jpg')) {
                          target.src = '/images/courses/c7s1.jpg';
                        } else if (target.src.endsWith('/images/courses/c6s1.jpg')) {
                          target.src = '/c6s1.jpg';
                        } else if (target.src.endsWith('/c6s1.jpg')) {
                          target.src = '/NEW_LOGO.webp';
                        } else {
                          target.style.display = 'none';
                        }
                      }}
                    />
                  </div>

                  {/* CARD BODY */}
                  <div className="p-6 flex flex-1 flex-col justify-between space-y-6">
                    
                    {/* TAGLINE, TITLE & SHORT DESCRIPTION */}
                    <div className="space-y-2">
                      <p className="font-sans font-black text-emerald-600 dark:text-emerald-400 text-[11px] uppercase tracking-wider">
                        "Every Question Sparks Success."
                      </p>
                      <h3 className="font-display font-extrabold text-base text-slate-900 dark:text-white leading-tight">
                        {course.title}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium line-clamp-2">
                        {course.description || `Master ${course.title} with interactive MCQs, chapter-wise practice and smart assessments designed to strengthen concepts and boost exam confidence.`}
                      </p>
                    </div>

                    {/* FOUR SMALL FEATURE ITEMS */}
                    <div className="flex flex-row items-center justify-between gap-1 py-3 border-y border-slate-100 dark:border-slate-800/60 text-center">
                      <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">
                        <span className="text-sm">📘</span>
                        <span className="font-display font-extrabold text-[11px] text-slate-800 dark:text-slate-200 leading-tight">
                          {course.id === '8-sci' ? '13' : course.id === '8-math' ? '14' : course.id === '8-social' ? '15' : course.id === '7-math' ? '15' : course.id === '7-social' ? '12' : (course.id === '7-sci' || course.id === '6-sci') ? '12' : course.id === '6-social' ? '14' : course.id === '6-math' ? '10' : '12'} Chapters
                        </span>
                      </div>
                      <div className="h-6 w-px bg-slate-100 dark:bg-slate-800/60"></div>
                      <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">
                        <span className="text-sm">❓</span>
                        <span className="font-display font-extrabold text-[11px] text-indigo-600 dark:text-indigo-400 leading-tight">
                          {course.id === '8-sci' ? '1200+' : course.id === '8-math' ? '1400+' : course.id === '8-social' ? '3000+' : course.id === '7-math' ? '1200+' : course.id === '7-social' ? '1200+' : (course.id === '7-sci' || course.id === '6-sci') ? '1000+' : course.id === '6-social' ? '1000+' : course.id === '6-math' ? '900+' : '1000+'} MCQs
                        </span>
                      </div>
                      <div className="h-6 w-px bg-slate-100 dark:bg-slate-800/60"></div>
                      <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">
                        <span className="text-sm">📝</span>
                        <span className="font-display font-extrabold text-[11px] text-slate-800 dark:text-slate-200 leading-tight">
                          {course.id === '8-sci' ? '130+' : course.id === '8-math' ? '180+' : course.id === '8-social' ? '120+' : course.id === '7-math' ? '150+' : course.id === '7-social' ? '150+' : (course.id === '7-sci' || course.id === '6-sci') ? '120+' : course.id === '6-social' ? '120+' : course.id === '6-math' ? '100+' : '120+'} Tests
                        </span>
                      </div>
                      <div className="h-6 w-px bg-slate-100 dark:bg-slate-800/60"></div>
                      <div className="flex-1 flex flex-col items-center justify-center space-y-0.5">
                        <span className="text-sm">📑</span>
                        <span className="font-display font-extrabold text-[11px] text-slate-800 dark:text-slate-200 leading-tight">Detailed Solutions</span>
                      </div>
                    </div>

                    {/* ACTIONS ROW */}
                    {user?.enrolledCourses?.includes(course.id) ? (
                      <div className="flex flex-col gap-2 pt-2">
                        <button
                          onClick={() => {
                            setPage('premium-course-details', { courseId: course.id });
                          }}
                          className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-xl font-extrabold text-xs flex items-center justify-center gap-1.5 cursor-pointer shadow-md transition-all hover:scale-[1.01]"
                          id={`premium-home-explore-btn-${course.id}`}
                        >
                          <span>Explore More</span>
                          <ChevronRight className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleOpenPdfLibrary(course.id)}
                          className="w-full py-2.5 px-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl font-extrabold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all duration-200"
                        >
                          <FileText className="h-4 w-4 text-emerald-600" />
                          <span>Study Material PDF</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 pt-2">
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              setPage('premium-course-details', { courseId: course.id });
                            }}
                            className="py-2.5 sm:py-3 px-2 sm:px-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700 rounded-xl font-extrabold text-[10px] sm:text-xs flex items-center justify-center cursor-pointer transition-all duration-200 whitespace-nowrap"
                            id={`premium-home-explore-btn-${course.id}`}
                          >
                            Explore More
                          </button>
                          
                          {launchOfferActive ? (
                            <button
                              onClick={() => {
                                guardPurchase(() => {
                                  setPage('premium-course-details', { courseId: course.id });
                                }, 'Please Login to Claim Premium', 'login');
                              }}
                              className="py-2.5 sm:py-3 px-2 sm:px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 rounded-xl font-black text-[10px] sm:text-xs flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-amber-500/10 transition-all duration-300 whitespace-nowrap"
                              id={`premium-home-claim-btn-${course.id}`}
                            >
                              Claim Premium
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                guardPurchase(() => {
                                  addToCart(course.id);
                                }, 'Please Login to Buy Course', 'login');
                              }}
                              className="py-2.5 sm:py-3 px-2 sm:px-4 bg-[#009874] hover:bg-[#008362] text-white rounded-xl font-extrabold text-[10px] sm:text-xs flex items-center justify-center cursor-pointer shadow-md transition-all duration-200 whitespace-nowrap"
                              id={`premium-home-buy-btn-${course.id}`}
                            >
                              Buy Course
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => handleOpenPdfLibrary(course.id)}
                          className="w-full py-2.5 px-4 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-950 rounded-xl font-extrabold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all duration-200"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Study Material PDF</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Practice Hub Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 mb-12">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 dark:text-white">
            Practise Hub
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-2xl mx-auto">
            Sharpen your concepts with Daily Practice and Premium Mock Tests.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* CARD 1: Daily Practice Test (PREMIUM) */}
          <PracticeHubCard
            title="Daily Practice Test"
            description="Practice today's chapter selected by the MCQ PULSE™ team. Fresh questions are updated regularly to strengthen your concepts."
            badgeText="PREMIUM"
            badgeGradient="linear-gradient(to right, #FBBF24, #F59E0B)"
            badgeShadow="0 8px 18px rgba(245,158,11,0.35)"
            Icon={BookOpen}
            iconBg="bg-indigo-50 dark:bg-indigo-950/40"
            iconColor="text-indigo-600 dark:text-indigo-400"
            buttonBg="bg-gradient-to-r from-amber-500 to-yellow-500"
            buttonHoverBg="hover:from-amber-400 hover:to-yellow-400"
            buttonTextColor="text-slate-950"
            buttonText="Start Practice"
            hasTrophyIconInButton={true}
            onClick={() => {
              const isPremiumUser = user?.isPremium || accessInfo.isPremiumStudent;
              if (!isPremiumUser) {
                const detectedClassNum = user?.currentClass
                  ? Number(user.currentClass.replace(/\D/g, ''))
                  : 8;
                setPurchaseTargetClass(detectedClassNum);
                setPurchaseTargetSubject('Science');
                setPremiumPopupType('daily');
                setShowPremiumPopup(true);
                return;
              }
              setPage('practice');
            }}
          />

          {/* CARD 2: Weekly Quiz (FREE) */}
          <PracticeHubCard
            title="Weekly Quiz"
            description="Take our FREE weekly quiz and challenge yourself every week with fresh topic questions."
            badgeText="FREE"
            badgeGradient="linear-gradient(to right, rgb(249, 115, 22), rgb(236, 72, 153))"
            badgeShadow="0 3px 10px rgba(249,115,22,0.4)"
            Icon={Trophy}
            iconBg="bg-indigo-50 dark:bg-indigo-950/40"
            iconColor="text-indigo-600 dark:text-indigo-400"
            buttonBg="bg-gradient-to-r from-indigo-600 to-indigo-500"
            buttonHoverBg="hover:from-indigo-500 hover:to-indigo-400"
            buttonTextColor="text-white"
            buttonText="Start Quiz"
            onClick={() => {
              if (!user) {
                setAuthModalOpen(true);
              } else {
                setPage('practice');
              }
            }}
          />

          {/* CARD 3: Mock Test (PREMIUM) */}
          <PracticeHubCard
            title="Mock Test"
            description="Attempt a complete NCERT mock test with detailed analysis, custom chapter selection, and performance tracking."
            badgeText="PREMIUM"
            badgeGradient="linear-gradient(to right, #FBBF24, #F59E0B)"
            badgeShadow="0 8px 18px rgba(245,158,11,0.35)"
            Icon={Award}
            iconBg="bg-indigo-50 dark:bg-indigo-950/40"
            iconColor="text-indigo-600 dark:text-indigo-400"
            buttonBg="bg-gradient-to-r from-amber-500 to-yellow-500"
            buttonHoverBg="hover:from-amber-400 hover:to-yellow-400"
            buttonTextColor="text-slate-950"
            buttonText="START MOCK TEST"
            hasTrophyIconInButton={true}
            onClick={() => {
              const isPremiumUser = user?.isPremium || accessInfo.isPremiumStudent;
              if (!isPremiumUser) {
                const detectedClassNum = user?.currentClass
                  ? Number(user.currentClass.replace(/\D/g, ''))
                  : 8;
                setPurchaseTargetClass(detectedClassNum);
                setPurchaseTargetSubject('Science');
                setPremiumPopupType('mock');
                setShowPremiumPopup(true);
                return;
              }
              setPage('practice');
            }}
          />

        </div>
      </section>

      {/* ----------------------------------------------------
          MODAL POPUP: PREMIUM PRACTICE TEST ACCESS
          ---------------------------------------------------- */}
      <PracticeTestAccessModal
        isOpen={showPremiumPopup}
        onClose={() => setShowPremiumPopup(false)}
        onStartPractice={() => {
          handleJoinPremium();
        }}
        targetClass={purchaseTargetClass}
        targetSubject={purchaseTargetSubject}
      />

    </div>
  );
};
